import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import StepNav, { ANAES_MACHINE_STEPS } from "@/components/StepNav";
import { IppvCircuit } from "@/components/ippv/IppvCircuit";
import { PhaseStepper, MachineDebrief } from "@/components/ippv/MachinePanels";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label as FieldLabel } from "@/components/ui/label";
import { Wrench, ChevronLeft, RotateCcw, Zap, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/* Anaesthetic Machine Setup — Phase 1 (initial checks + assembly) and
   Phase 2 (low-pressure leak test). Ends after a passing leak-holds. The
   ventilation phase lives in its own /ippv simulator so this one can be
   completed independently for the machine-preparation competency. */

const PHASES = [
  { key: "setup", label: "Initial setup" },
  { key: "leak", label: "Leak test" },
];
const ORDER = ["setup", "leak", "done"];

// Reservoir-bag sizing rule of thumb (≥6× tidal volume): pick by patient weight.
const bagFor = (kg) => (kg <= 7 ? "1L" : kg <= 20 ? "2L" : kg <= 40 ? "3L" : "5L");

// Circuit-selection cases: rebreathing (circle) vs non-rebreathing by weight.
const PATIENT_CASES = [
  { id: "cat", label: "Cat", weight: 4.2, correct: "non", reason: "Under ~7 kg — a non-rebreathing system (e.g. Ayre's T-piece / Bain) has low resistance and minimal dead space for a small patient." },
  { id: "smalldog", label: "Small dog", weight: 6.5, correct: "non", reason: "Under ~7 kg — use a non-rebreathing system to minimise resistance and the work of breathing." },
  { id: "dog", label: "Dog", weight: 24, correct: "re", reason: "Over ~7–10 kg — a rebreathing (circle) system is efficient and conserves heat and moisture." },
  { id: "largedog", label: "Large dog", weight: 38, correct: "re", reason: "Well over 10 kg — a rebreathing (circle) system is the appropriate, economical choice." },
];
const pickCase = () => PATIENT_CASES[Math.floor(Math.random() * PATIENT_CASES.length)];

export default function AnaesMachine() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("setup"); // setup | leak | done

  /* ── PHASE 1 · setup ── */
  const [o2On, setO2On] = useState(false);
  const [spareChecked, setSpareChecked] = useState(false);
  const [flow, setFlow] = useState(0);
  const [vapFilled, setVapFilled] = useState(false);
  const [vaporiserPct, setVaporiserPct] = useState(0);
  const [sodaLimeChecked, setSodaLimeChecked] = useState(false);
  const [circuitChecked, setCircuitChecked] = useState(false);
  const [scavengingOn, setScavengingOn] = useState(false);
  const [preuseSigned, setPreuseSigned] = useState(false);
  const [weightCase, setWeightCase] = useState(pickCase);
  const [circuitChoice, setCircuitChoice] = useState(null);
  const [bagSize, setBagSize] = useState(null);

  /* ── PHASE 2 · leak test ── */
  const [aplClosed, setAplClosed] = useState(false);
  const [occluded, setOccluded] = useState(false);
  const [leakP, setLeakP] = useState(0);
  const [leakFixed, setLeakFixed] = useState(false);
  const [leakPass, setLeakPass] = useState(false);

  /* ── challenge + scoring ── */
  const [challenge, setChallenge] = useState(false);
  const [mistakes, setMistakes] = useState([]);
  const [result, setResult] = useState(null);
  const mistakeRef = useRef([]);

  const correctBag = bagFor(weightCase.weight);
  const circuitSelected = circuitChoice === weightCase.correct;
  const leakPresent = challenge;
  const leaking = leakPresent && !leakFixed;

  const setupCoreReady =
    o2On && spareChecked && flow > 0 && vapFilled && vaporiserPct > 0 &&
    sodaLimeChecked && circuitSelected && circuitChecked &&
    bagSize === correctBag && scavengingOn;
  const setupComplete = setupCoreReady && preuseSigned;

  const fail = (p, msg) => {
    mistakeRef.current = [...mistakeRef.current, { phase: p, msg }];
    setMistakes(mistakeRef.current);
    toast.error(msg);
  };

  /* ── setup actions ── */
  const onO2 = () => {
    if (o2On) return;
    if (!circuitSelected) return fail("setup", "Choose the correct breathing system before pressurising the machine");
    setO2On(true); toast.success("Main O₂ supply open");
  };
  const onSpare = () => { if (spareChecked) return; setSpareChecked(true); toast.success("Full spare O₂ cylinder confirmed"); };
  const onFlow = () => {
    if (flow > 0) return;
    if (!circuitSelected) return fail("setup", "Choose the breathing system before setting O₂ flow");
    if (!o2On) return fail("setup", "Open the O₂ supply before setting the flowmeter");
    setFlow(2); toast.success("O₂ flow set to 2 L/min");
  };
  const onVapFill = () => {
    if (!circuitSelected) return fail("setup", "Choose the breathing system before touching the vaporiser");
    setVapFilled(true); toast.success("Vaporiser filled & seated");
  };
  const onSetVap = () => { setVaporiserPct(2); toast.success("Vaporiser set to maintenance (2%)"); };
  const onSodaLime = () => { if (sodaLimeChecked) return; setSodaLimeChecked(true); toast.success("Soda lime fresh — CO₂ absorbent OK"); };
  const onCircuit = () => {
    if (circuitChecked) return;
    if (!circuitSelected) return fail("setup", "Choose the correct breathing system for this patient first");
    if (bagSize !== correctBag) return fail("setup", "Select the correct reservoir-bag size before connecting the circuit");
    setCircuitChecked(true); toast.success("Circuit & reservoir bag connected");
  };
  const onScavenging = () => { if (scavengingOn) return; setScavengingOn(true); toast.success("Scavenging (AGSS) connected"); };
  const onPreuse = () => {
    if (preuseSigned) return;
    if (!setupCoreReady) return fail("setup", "Finish every machine check before signing the pre-use checklist");
    setPreuseSigned(true); toast.success("Pre-use safety checklist signed");
  };
  const onBagSize = (b) => {
    if (bagSize === correctBag) return;
    setBagSize(b);
    if (b === correctBag) toast.success(`Correct — a ${b} bag suits a ${weightCase.weight} kg patient`);
    else fail("setup", `A ${b} reservoir bag isn't ideal for ${weightCase.weight} kg — aim for ≥6× tidal volume`);
  };
  const onCircuitChoice = (choice) => {
    if (circuitSelected) return;
    setCircuitChoice(choice);
    if (choice === weightCase.correct) toast.success(`Correct circuit for a ${weightCase.weight} kg patient`);
    else fail("setup", `A ${choice === "re" ? "rebreathing" : "non-rebreathing"} circuit isn't ideal for ${weightCase.weight} kg`);
  };

  /* ── leak-test actions ── */
  const onCloseApl = () => { setAplClosed(true); toast.success("APL (pop-off) valve closed"); };
  const onOcclude = () => { setOccluded(true); toast.success("Patient Y-piece occluded"); };
  const onFlush = () => {
    if (!aplClosed || !occluded)
      return fail("leak", "Close the APL valve and occlude the patient port before pressurising");
    setLeakP((p) => (leaking ? Math.min(16, p + 10) : Math.min(34, p + 10)));
  };
  const onFixLeak = () => { setLeakFixed(true); toast.success("Loose connection found & tightened — leak fixed"); };

  /* ── click dispatchers on the diagram ── */
  const onVaporiserClick = () => {
    if (phase === "setup") {
      if (!vapFilled) return onVapFill();
      if (vaporiserPct === 0) return onSetVap();
      return toast.message("Vaporiser already set to 2%");
    }
  };
  const onBagClick = () => {
    if (phase === "setup") return onCircuit();
    if (phase === "leak") return onFlush();
  };
  const onPopoffClick = () => {
    if (phase === "leak") return aplClosed ? toast.message("APL valve already closed") : onCloseApl();
  };
  const onPatientClick = () => {
    if (phase === "leak") return occluded ? toast.message("Patient port already occluded") : onOcclude();
  };

  /* ── auto-advance between phases ── */
  useEffect(() => {
    if (phase === "setup" && setupComplete) {
      setPhase("leak");
      toast.message("Setup complete — now leak-test the circuit");
    }
  }, [phase, setupComplete]);

  useEffect(() => {
    if (phase === "leak" && !leaking && leakP >= 30 && !leakPass) {
      setLeakPass(true);
      toast.success("Leak test passed — circuit holds 30 cmH₂O. Machine ready.");
      finish();
    }
  }, [phase, leaking, leakP, leakPass, finish]);

  const finish = useCallback(() => {
    const m = mistakeRef.current;
    const count = (p) => m.filter((x) => x.phase === p).length;
    // Machine-only weighting: 60 setup + 40 leak = 100.
    const setupScore = Math.max(0, 60 - count("setup") * 8);
    const leakScore = Math.max(0, 40 - count("leak") * 10);
    const total = setupScore + leakScore;
    const grade = total >= 90 ? "A" : total >= 80 ? "B" : total >= 70 ? "C" : total >= 60 ? "D" : "F";
    setResult({
      total, grade, mistakes: m,
      phases: [
        { key: "setup", label: "Initial setup", score: setupScore, max: 60 },
        { key: "leak", label: "Leak test", score: leakScore, max: 40 },
      ],
    });
    setPhase("done");
  }, []);

  const reset = () => {
    mistakeRef.current = [];
    setPhase("setup");
    setO2On(false); setSpareChecked(false); setFlow(0); setVapFilled(false);
    setVaporiserPct(0); setSodaLimeChecked(false); setCircuitChecked(false);
    setScavengingOn(false); setPreuseSigned(false); setBagSize(null);
    setCircuitChoice(null); setWeightCase(pickCase());
    setAplClosed(false); setOccluded(false); setLeakP(0); setLeakFixed(false); setLeakPass(false);
    setMistakes([]); setResult(null);
  };

  const setChallengeMode = (on) => {
    setChallenge(on); reset(); setChallenge(on);
    toast.message(on
      ? "Challenge mode ON — a hidden leak to find at the leak test"
      : "Challenge mode OFF — standard guided workflow");
  };

  /* ── guided "next action" ── */
  const next = (() => {
    if (phase === "setup") {
      // Circuit-first: never pressurise the machine before the right breathing
      // system is chosen and connected for this patient.
      if (!circuitSelected) return { node: "circuit-type", hint: "Choose the right breathing system for the patient" };
      if (bagSize !== correctBag) return { node: "bag-size", hint: "Choose the correct reservoir-bag size" };
      if (!circuitChecked) return { node: "bag", hint: "Connect & check the circuit + reservoir bag" };
      if (!o2On) return { node: "o2", hint: "Open the main O₂ cylinder" };
      if (!spareChecked) return { node: "spare", hint: "Check the spare O₂ cylinder is full" };
      if (flow === 0) return { node: "flowmeter", hint: "Set the O₂ flowmeter" };
      if (!vapFilled) return { node: "vaporiser", hint: "Check & fill the vaporiser" };
      if (vaporiserPct === 0) return { node: "vaporiser", hint: "Set the vaporiser to 2%" };
      if (!sodaLimeChecked) return { node: "sodalime", hint: "Check the soda lime (CO₂ absorbent)" };
      if (!scavengingOn) return { node: "scavenging", hint: "Connect the scavenging system (AGSS)" };
      if (!preuseSigned) return { node: "clipboard", hint: "Sign the pre-use safety checklist" };
      return null;
    }
    if (phase === "leak") {
      if (!aplClosed) return { node: "popoff", hint: "Close the APL (pop-off) valve" };
      if (!occluded) return { node: "patient", hint: "Occlude the patient Y-piece" };
      if (leaking && leakP >= 16) return { node: "circuit", hint: "Pressure stalls — find & fix the leak" };
      if (leakP < 30) return { node: "bag", hint: "Squeeze the bag to pressurise to 30 cmH₂O" };
      return null;
    }
    return null;
  })();

  // manometer geometry
  const displayPressure = phase === "leak" ? leakP : 0;
  const gasFlowing = o2On && flow > 0;
  const popClosed = phase === "leak" ? aplClosed : false;
  const ang = (-120 + (Math.min(displayPressure, 60) / 60) * 240) * (Math.PI / 180);
  const nx = 60 + 38 * Math.cos(ang - Math.PI / 2);
  const ny = 60 + 38 * Math.sin(ang - Math.PI / 2);
  const phaseStep = phase === "setup" ? 1 : 2;

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav current={phaseStep} steps={ANAES_MACHINE_STEPS} title="Anaesthetic Machine Setup" subtitle="Machine checks · assembly · leak test" />
      <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <Wrench className="w-3.5 h-3.5" />
            <span>Anaesthetic machine</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Anaesthetic Machine Setup</h1>
          <p className="text-base text-slate-600 mt-2 max-w-2xl">
            Prepare a safe anaesthetic machine for a real patient. <span className="font-semibold text-slate-900">Click the equipment</span> in
            the diagram to operate it — work through initial checks, correct circuit selection, and finish with a
            <span className="font-semibold text-slate-900"> low-pressure leak test</span> that holds 30 cmH₂O. To ventilate a patient,
            complete the separate <span className="font-semibold text-slate-900">IPPV</span> simulator.
          </p>
        </header>

        <div className="mb-6"><PhaseStepper phase={phase} phases={PHASES} order={ORDER} /></div>

        {phase === "done" ? (
          <div className="space-y-5">
            <MachineDebrief
              result={result}
              onReset={reset}
              simulatorId="anaesmachine"
              simulatorName="Anaesthetic Machine Setup"
              gradeContext="Cert IV anaesthetic machine preparation"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => navigate("/ippv")}
                data-testid="continue-to-ippv-btn"
                className="bg-[#0047AB] hover:bg-[#003380] text-white"
              >
                Continue to IPPV simulator <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" onClick={() => navigate("/sections")} data-testid="back-to-home-btn" className="text-slate-600">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Simulator Hub
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="space-y-4">
              <HintCard next={next} />
              {phase === "setup" && (
                <DecisionsCard
                  weightCase={weightCase} circuitChoice={circuitChoice} circuitSelected={circuitSelected}
                  onCircuitChoice={onCircuitChoice} bagSize={bagSize} correctBag={correctBag} onBagSize={onBagSize}
                  next={next}
                />
              )}
              <ChallengeCard challenge={challenge} setChallengeMode={setChallengeMode} />

              {mistakes.length > 0 && (
                <div className="text-[11px] text-slate-500" data-testid="live-mistakes">
                  {mistakes.length} note{mistakes.length === 1 ? "" : "s"} logged this run
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={reset} data-testid="anaes-machine-reset-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white flex-1 sm:flex-none">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
                </Button>
                <Button variant="outline" onClick={() => navigate("/sections")} data-testid="back-to-home-btn" className="text-slate-600 flex-1 sm:flex-none">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back to Hub
                </Button>
              </div>
            </div>

            <div className="lg:col-span-2">
              <IppvCircuit
                phase={phase} next={next} onFixLeak={onFixLeak}
                gasFlowing={gasFlowing} o2On={o2On} spareChecked={spareChecked} flow={flow}
                vapFilled={vapFilled} vaporiserPct={vaporiserPct} vaporiserOff={false} vaporiserBackOn={false}
                sodaLimeChecked={sodaLimeChecked} circuitChecked={circuitChecked} scavengingOn={scavengingOn}
                preuseSigned={preuseSigned} setupCoreReady={setupCoreReady}
                aplClosed={aplClosed} occluded={occluded} leaking={leaking} popClosed={popClosed}
                breathsDone={false} breathCount={0}
                displayPressure={displayPressure} nx={nx} ny={ny} lungsInflated={false} bagOverfilled={false}
                onO2={onO2} onSpare={onSpare} onFlow={onFlow} onVaporiser={onVaporiserClick} onSodaLime={onSodaLime}
                onBag={onBagClick} onPopoff={onPopoffClick} onPatient={onPatientClick} onScavenging={onScavenging} onClipboard={onPreuse}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/* ── shared side-rail cards ─────────────────────────────────────── */
const HintCard = ({ next }) => (
  <Card className="border-[#0047AB]/30 bg-[#0047AB]/[0.03]" data-testid="anaes-machine-hint-card">
    <CardContent className="pt-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-1.5">Next action</div>
      {next ? (
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 w-6 h-6 rounded-full bg-[#0047AB] text-white flex items-center justify-center shrink-0 animate-pulse">
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
          <p className="text-sm text-slate-800 font-medium leading-snug" data-testid="anaes-machine-next-hint">{next.hint}</p>
        </div>
      ) : (
        <p className="text-sm text-emerald-700 font-medium" data-testid="anaes-machine-next-hint">Phase complete — well done.</p>
      )}
      <p className="text-[11px] text-slate-500 mt-2.5 leading-relaxed">Operate the machine by clicking the equipment in the diagram →</p>
    </CardContent>
  </Card>
);

const DecisionsCard = ({ weightCase, circuitChoice, circuitSelected, onCircuitChoice, bagSize, correctBag, onBagSize, next }) => {
  const BAGS = ["0.5L", "1L", "2L", "3L", "5L"];
  const bagDone = bagSize === correctBag;
  return (
    <Card className="border-slate-200" data-testid="decisions-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <Wrench className="w-4 h-4 text-[#0047AB]" /> Clinical decisions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-slate-600">
          Patient: <span className="font-semibold text-slate-900" data-testid="circuit-patient">{weightCase.label} · {weightCase.weight} kg</span>
        </div>

        <div className={`rounded-lg p-2 -m-2 transition-all ${next?.node === "circuit-type" ? "ring-2 ring-[#0047AB]/30 bg-[#0047AB]/[0.03]" : ""}`}>
          <div className="text-[12px] font-semibold text-slate-700 mb-1.5">Breathing system</div>
          <div className="grid grid-cols-2 gap-2">
            {[{ id: "re", t: "Rebreathing (circle)" }, { id: "non", t: "Non-rebreathing (Bain / T-piece)" }].map((o) => {
              let cls = "border-slate-200 bg-white text-slate-700 hover:border-[#0047AB]";
              if (circuitSelected) cls = o.id === weightCase.correct ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-400 opacity-60";
              else if (circuitChoice === o.id) cls = "border-rose-400 bg-rose-50 text-rose-700";
              return (
                <button key={o.id} type="button" disabled={circuitSelected} onClick={() => onCircuitChoice(o.id)} data-testid={`circuit-choice-${o.id}`}
                  className={`text-left text-[13px] font-semibold border-2 rounded-md px-3 py-2.5 leading-tight transition-colors disabled:cursor-default ${cls}`}>
                  {o.t}
                </button>
              );
            })}
          </div>
          {circuitSelected && (
            <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mt-2" data-testid="circuit-reason">{weightCase.reason}</div>
          )}
        </div>

        <div className={`rounded-lg p-2 -m-2 transition-all ${next?.node === "bag-size" ? "ring-2 ring-[#0047AB]/30 bg-[#0047AB]/[0.03]" : ""}`}>
          <div className="text-[12px] font-semibold text-slate-700 mb-1.5">Reservoir-bag size <span className="font-normal text-slate-400">(≥6× tidal volume)</span></div>
          <div className="flex flex-wrap gap-1.5" data-testid="bag-size-row">
            {BAGS.map((b) => {
              const isCorrect = bagDone && b === correctBag;
              const isWrong = bagSize === b && b !== correctBag;
              let cls = "border-slate-200 bg-white text-slate-600 hover:border-[#0047AB]";
              if (isCorrect) cls = "border-emerald-400 bg-emerald-50 text-emerald-700";
              else if (isWrong) cls = "border-rose-400 bg-rose-50 text-rose-700";
              return (
                <button key={b} type="button" onClick={() => onBagSize(b)} disabled={bagDone} data-testid={`bag-size-${b}`}
                  className={`text-xs font-semibold border-2 rounded-md px-2.5 py-1 transition-colors disabled:cursor-default ${cls}`}>
                  {b}
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ChallengeCard = ({ challenge, setChallengeMode }) => (
  <Card className={`border-2 transition-colors ${challenge ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`} data-testid="challenge-card">
    <CardContent className="pt-5 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${challenge ? "bg-amber-500" : "bg-slate-200"}`}>
        <Zap className={`w-4 h-4 ${challenge ? "text-white" : "text-slate-500"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <FieldLabel htmlFor="challenge-toggle" className="text-sm font-semibold text-slate-900 cursor-pointer">Challenge mode</FieldLabel>
          <Switch id="challenge-toggle" checked={challenge} onCheckedChange={setChallengeMode} data-testid="challenge-toggle" />
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
          Hides a leak in the circuit for you to find during the leak test — pressure will stall around 16 cmH₂O until you locate and tighten the offending connection.
        </p>
      </div>
    </CardContent>
  </Card>
);
